import { parseAndCleanJSON } from './gemini';

const suggestionsCache = new Map<string, string[]>();

/**
 * Standard curriculum chapter database mapped strictly by Grade Level and Subject.
 * Ensures 100% syllabus accuracy for CBSE, ICSE, State Boards, IB, and General.
 */
export const SYLLABUS_DATABASE: Record<string, Record<string, string[]>> = {
  'Class 10': {
    Mathematics: [
      'Quadratic Equations',
      'Arithmetic Progressions (AP)',
      'Real Numbers & Polynomials',
      'Pair of Linear Equations in Two Variables',
      'Coordinate Geometry',
      'Triangles & Similarity Theorems',
      'Introduction to Trigonometry & Heights and Distances',
      'Circles & Areas Related to Circles',
      'Surface Areas and Volumes',
      'Statistics & Probability'
    ],
    Physics: [
      'Light - Reflection and Refraction',
      'The Human Eye and the Colourful World',
      'Electricity (Ohm\'s Law & Resistance)',
      'Magnetic Effects of Electric Current',
      'Sources of Energy'
    ],
    Chemistry: [
      'Chemical Reactions and Equations',
      'Acids, Bases and Salts',
      'Metals and Non-Metals',
      'Carbon and Its Compounds',
      'Periodic Classification of Elements'
    ],
    Biology: [
      'Life Processes (Nutrition, Respiration, Circulation, Excretion)',
      'Control and Coordination',
      'How do Organisms Reproduce?',
      'Heredity and Evolution',
      'Our Environment'
    ],
    'Computer Science': [
      'Python Programming Fundamentals',
      'Cyber Ethics & Online Safety',
      'HTML / CSS Web Page Design',
      'Scratch & Computational Thinking'
    ],
    Programming: [
      'Python Basics - Loops & Conditionals',
      'Functions and Modular Code in Python',
      'Data Types, Lists & Strings in Python',
      'Algorithm Building & Flowcharts'
    ],
    'Economics': [
      'Development & Economic Indicators',
      'Sectors of the Indian Economy',
      'Money and Credit',
      'Globalization and the Indian Economy',
      'Consumer Rights'
    ],
    'History & Civics': [
      'The Rise of Nationalism in Europe',
      'Nationalism in India',
      'The Making of a Global World',
      'Power Sharing & Federalism',
      'Gender, Religion and Caste',
      'Political Parties & Outcomes of Democracy'
    ],
    'Geography': [
      'Resources and Development',
      'Forest and Wildlife Resources',
      'Water Resources & Agriculture',
      'Minerals and Energy Resources',
      'Manufacturing Industries',
      'Lifelines of National Economy'
    ],
    Statistics: [
      'Mean, Median and Mode of Grouped Data',
      'Cumulative Frequency Graphs (Ogive)',
      'Theoretical & Experimental Probability'
    ],
    'Logical Reasoning': [
      'Number & Letter Series',
      'Direction Sense & Blood Relations',
      'Venn Diagrams & Syllogisms',
      'Coding-Decoding & Analogy'
    ]
  },

  'Class 9': {
    Mathematics: [
      'Number Systems (Rational & Irrational Numbers)',
      'Polynomials & Remainder Theorem',
      'Coordinate Geometry',
      'Linear Equations in Two Variables',
      'Lines and Angles',
      'Triangles (Congruence Criteria)',
      'Quadrilaterals & Parallelograms',
      'Circles & Circle Theorems',
      'Heron\'s Formula for Triangle Area',
      'Surface Areas and Volumes',
      'Statistics (Bar Graphs & Histograms)',
      'Probability Basics'
    ],
    Physics: [
      'Motion & Equations of Uniform Acceleration',
      'Force and Newton\'s Laws of Motion',
      'Gravitation, Mass and Weight',
      'Work, Energy and Conservation of Energy',
      'Sound - Propagation, Echo & Ultrasound'
    ],
    Chemistry: [
      'Matter in Our Surroundings',
      'Is Matter Around Us Pure (Mixtures & Solutions)',
      'Atoms and Molecules (Mole Concept & Formulae)',
      'Structure of the Atom (Electrons, Protons, Neutrons & Isotopes)'
    ],
    Biology: [
      'The Fundamental Unit of Life (Cell & Organelles)',
      'Tissues (Plant & Animal Tissues)',
      'Improvement in Food Resources & Agriculture'
    ],
    'Computer Science': [
      'Computer Fundamentals & Hardware',
      'Basics of Python / Scratch Programming',
      'Cyber Safety & Digital Citizenship'
    ],
    'History & Civics': [
      'The French Revolution',
      'Socialism in Europe and the Russian Revolution',
      'Nazism and the Rise of Hitler',
      'What is Democracy? Why Democracy?',
      'Constitutional Design & Electoral Politics'
    ],
    'Geography': [
      'India - Size and Location',
      'Physical Features of India',
      'Drainage & River Systems',
      'Climate and Monsoon of India',
      'Natural Vegetation and Wildlife'
    ],
    Economics: [
      'The Story of Village Palampur',
      'People as Resource',
      'Poverty as a Challenge Facing India',
      'Food Security in India'
    ]
  },

  'Class 11': {
    Mathematics: [
      'Sets, Relations and Functions',
      'Trigonometric Functions & Graphs',
      'Complex Numbers & Quadratic Equations',
      'Linear Inequalities',
      'Permutations and Combinations',
      'Binomial Theorem',
      'Sequences and Series (AP & GP)',
      'Straight Lines & Conic Sections (Parabola, Ellipse, Hyperbola)',
      'Introduction to Three Dimensional Geometry',
      'Limits and Derivatives (Calculus Foundations)',
      'Statistics (Mean Deviation & Variance)',
      'Probability'
    ],
    Physics: [
      'Units, Dimensions and Error Analysis',
      'Motion in a Straight Line & Motion in a Plane (Vectors & Projectile)',
      'Laws of Motion, Friction & Circular Dynamics',
      'Work, Energy and Power & Collisions',
      'System of Particles & Rotational Motion (Moment of Inertia & Torque)',
      'Gravitation & Kepler\'s Laws',
      'Mechanical Properties of Solids (Elasticity)',
      'Mechanical Properties of Fluids (Viscosity, Bernoulli)',
      'Thermal Properties of Matter & Thermodynamics',
      'Kinetic Theory of Gases',
      'Oscillations (Simple Harmonic Motion) and Waves'
    ],
    Chemistry: [
      'Some Basic Concepts of Chemistry (Mole Concept & Stoichiometry)',
      'Structure of Atom (Bohr Model & Quantum Numbers)',
      'Classification of Elements and Periodicity in Properties',
      'Chemical Bonding and Molecular Structure (VSEPR, Hybridization, MOT)',
      'Chemical Thermodynamics (Enthalpy, Entropy & Gibbs Free Energy)',
      'Equilibrium (Chemical & Ionic Equilibrium, pH, Buffers)',
      'Redox Reactions & Oxidation Numbers',
      'Organic Chemistry - Some Basic Principles and Techniques (GOC)',
      'Hydrocarbons (Alkanes, Alkenes, Alkynes & Aromatic Rings)'
    ],
    Biology: [
      'The Living World & Biological Classification',
      'Plant Kingdom & Animal Kingdom',
      'Morphology and Anatomy of Flowering Plants',
      'Cell: The Unit of Life & Cell Cycle and Cell Division',
      'Photosynthesis in Higher Plants & Respiration in Plants',
      'Plant Growth and Development',
      'Human Physiology - Breathing and Exchange of Gases',
      'Body Fluids and Circulation',
      'Excretory Products and their Elimination',
      'Locomotion, Movement & Neural Control'
    ],
    'Computer Science': [
      'Computer Systems and Organization',
      'Computational Thinking and Python Programming',
      'Functions, Strings, Lists, Tuples, Dictionaries in Python',
      'Society, Law and Ethics'
    ],
    Economics: [
      'Statistics for Economics - Collection & Presentation of Data',
      'Measures of Central Tendency & Dispersion',
      'Correlation & Index Numbers',
      'Introductory Microeconomics - Consumer\'s Equilibrium & Demand',
      'Producer Behaviour and Supply',
      'Forms of Market and Price Determination'
    ]
  },

  'Class 12': {
    Mathematics: [
      'Relations and Functions & Inverse Trigonometric Functions',
      'Matrices and Determinants',
      'Continuity and Differentiability',
      'Applications of Derivatives (AOD - Tangents, Maxima & Minima)',
      'Integrals (Indefinite & Definite Integration)',
      'Applications of Integrals (Area Under Curves)',
      'Differential Equations (Order, Degree & Solutions)',
      'Vector Algebra',
      'Three Dimensional Geometry (3D Lines & Planes)',
      'Linear Programming (LPP)',
      'Probability (Conditional Probability & Bayes\' Theorem)'
    ],
    Physics: [
      'Electric Charges and Fields (Gauss\'s Law)',
      'Electrostatic Potential and Capacitance',
      'Current Electricity (Ohm\'s Law, Kirchhoff\'s Laws & Potentiometer)',
      'Moving Charges and Magnetism (Biot-Savart & Ampere\'s Law)',
      'Magnetism and Matter',
      'Electromagnetic Induction (Faraday & Lenz Laws)',
      'Alternating Current (LCR Circuits & Transformers)',
      'Electromagnetic Waves',
      'Ray Optics and Optical Instruments (Lenses & Mirrors)',
      'Wave Optics (Interference, Young\'s Double Slit & Diffraction)',
      'Dual Nature of Radiation and Matter (Photoelectric Effect)',
      'Atoms and Nuclei (Bohr Model & Radioactivity)',
      'Semiconductor Electronics (p-n Junction, Diodes & Logic Gates)'
    ],
    Chemistry: [
      'Solutions (Colligative Properties & Raoult\'s Law)',
      'Electrochemistry (Nernst Equation, Galvanic Cells & Batteries)',
      'Chemical Kinetics (Rate Laws & Arrhenius Equation)',
      'The d- and f-Block Elements',
      'Coordination Compounds (CFT, VBT & Isomerism)',
      'Haloalkanes and Haloarenes',
      'Alcohols, Phenols and Ethers',
      'Aldehydes, Ketones and Carboxylic Acids',
      'Amines (Organic Nitrogen Compounds)',
      'Biomolecules (Carbohydrates, Proteins & Nucleic Acids)'
    ],
    Biology: [
      'Sexual Reproduction in Flowering Plants',
      'Human Reproduction & Reproductive Health',
      'Principles of Inheritance and Variation (Genetics & Mendelian Laws)',
      'Molecular Basis of Inheritance (DNA Replication, Transcription, Translation)',
      'Evolution',
      'Human Health and Disease & Microbes in Human Welfare',
      'Biotechnology: Principles and Processes (Genetic Engineering)',
      'Biotechnology and its Applications',
      'Organisms and Populations & Ecosystems',
      'Biodiversity and Conservation'
    ],
    'Computer Science': [
      'Computational Thinking and Python - Data Structures (Stacks & Queues)',
      'Computer Networks - Topologies, Protocols & OSI Layers',
      'Database Management & SQL Queries (Joins, Aggregations)',
      'Python-SQL Interface'
    ],
    Economics: [
      'National Income and Related Aggregates',
      'Money and Banking (Central Bank & Commercial Banking)',
      'Determination of Income and Employment (Aggregate Demand & Multiplier)',
      'Government Budget and the Economy',
      'Balance of Payments and Foreign Exchange',
      'Indian Economic Development - Policy & Reforms'
    ]
  },

  'Class 8': {
    Mathematics: [
      'Rational Numbers',
      'Linear Equations in One Variable',
      'Understanding Quadrilaterals',
      'Data Handling (Bar Graphs & Pie Charts)',
      'Squares and Square Roots & Cubes and Cube Roots',
      'Comparing Quantities (Profit, Loss, Compound Interest)',
      'Algebraic Expressions and Identities',
      'Mensuration (Surface Area and Volume)',
      'Exponents and Powers',
      'Direct and Inverse Proportions',
      'Factorisation',
      'Introduction to Graphs'
    ],
    Physics: [
      'Force and Pressure',
      'Friction - Types and Applications',
      'Sound - Vibration, Frequency and Pitch',
      'Chemical Effects of Electric Current',
      'Some Natural Phenomena (Lightning & Earthquakes)',
      'Light - Laws of Reflection & Human Eye'
    ],
    Chemistry: [
      'Synthetic Fibres and Plastics',
      'Materials: Metals and Non-Metals',
      'Coal and Petroleum (Fossil Fuels)',
      'Combustion and Flame'
    ],
    Biology: [
      'Crop Production and Management',
      'Microorganisms: Friend and Foe',
      'Conservation of Plants and Animals',
      'Reproduction in Animals',
      'Reaching the Age of Adolescence',
      'Pollution of Air and Water'
    ]
  },

  'Class 7': {
    Mathematics: [
      'Integers (Operations & Properties)',
      'Fractions and Decimals',
      'Data Handling (Mean, Median, Mode)',
      'Simple Equations',
      'Lines and Angles',
      'The Triangle and its Properties & Congruence',
      'Comparing Quantities (Percentages & Simple Interest)',
      'Rational Numbers',
      'Practical Geometry & Perimeter and Area',
      'Algebraic Expressions',
      'Exponents and Powers'
    ],
    Physics: [
      'Heat and Temperature',
      'Motion and Time (Speed & Distance-Time Graphs)',
      'Electric Current and its Effects (Circuits & Electromagnets)',
      'Light (Mirrors, Lenses & Prisms)'
    ],
    Chemistry: [
      'Acids, Bases and Salts',
      'Physical and Chemical Changes',
      'Water: A Precious Resource'
    ],
    Biology: [
      'Nutrition in Plants (Photosynthesis)',
      'Nutrition in Animals (Digestion)',
      'Respiration in Organisms',
      'Transportation in Animals and Plants',
      'Reproduction in Plants',
      'Forests: Our Lifeline'
    ]
  },

  'Class 6': {
    Mathematics: [
      'Knowing Our Numbers (Place Value & Large Numbers)',
      'Whole Numbers & Number Line',
      'Playing with Numbers (Factors, Multiples, HCF & LCM)',
      'Basic Geometrical Ideas (Points, Lines, Polygons, Angles)',
      'Understanding Elementary Shapes',
      'Integers',
      'Fractions and Decimals',
      'Data Handling',
      'Mensuration (Perimeter & Area)',
      'Algebra - Introduction to Variables',
      'Ratio and Proportion'
    ],
    Physics: [
      'Motion and Measurement of Distances',
      'Light, Shadows and Reflections',
      'Electricity and Circuits',
      'Fun with Magnets'
    ],
    Chemistry: [
      'Sorting Materials into Groups',
      'Separation of Substances',
      'Air Around Us & Water'
    ],
    Biology: [
      'Components of Food & Balanced Diet',
      'Getting to Know Plants (Herbs, Shrubs, Trees, Leaves, Flowers)',
      'Body Movements (Joints & Skeleton)',
      'The Living Organisms — Characteristics and Habitats'
    ]
  },

  'Undergraduate / College': {
    Mathematics: [
      'Linear Algebra - Vector Spaces & Eigenvalues',
      'Multivariable Calculus - Partial Derivatives & Multiple Integrals',
      'Ordinary Differential Equations (ODE)',
      'Partial Differential Equations (PDE)',
      'Complex Analysis & Contour Integration',
      'Fourier Series & Laplace Transforms',
      'Probability Theory & Stochastic Processes',
      'Numerical Methods & Optimization'
    ],
    Physics: [
      'Classical Mechanics - Lagrangian & Hamiltonian Dynamics',
      'Electrodynamics & Maxwell\'s Equations',
      'Quantum Mechanics - Schrödinger Equation & Wavefunctions',
      'Statistical Mechanics & Thermodynamics',
      'Solid State Physics & Band Theory',
      'Nuclear and Particle Physics'
    ],
    Chemistry: [
      'Advanced Organic Synthesis & Reaction Mechanisms',
      'Quantum Chemistry & Spectroscopy',
      'Coordination Chemistry & Organometallics',
      'Chemical Thermodynamics & Statistical Mechanics',
      'Analytical Chemistry & Chromatography'
    ],
    Biology: [
      'Molecular Cell Biology & Signal Transduction',
      'Recombinant DNA Technology & Genomics',
      'Immunology & Immunotechnology',
      'Microbiology & Microbial Genetics',
      'Biochemistry & Enzymology'
    ],
    'Computer Science': [
      'Data Structures & Algorithms (Trees, Graphs, DP)',
      'Operating Systems - Process Scheduling & Memory Management',
      'Database Management Systems (DBMS) - Relational Algebra & SQL',
      'Computer Networks - TCP/IP, Routing & Protocols',
      'Theory of Computation & Automata Theory',
      'Compiler Design',
      'Artificial Intelligence & Machine Learning'
    ],
    Programming: [
      'Object-Oriented Design & Design Patterns',
      'Full Stack Web Development (React & Node.js)',
      'Concurrent & Distributed Systems Programming',
      'Data Structures & Algorithms in C++ / Java / Python'
    ],
    Engineering: [
      'Circuit Theory & Kirchhoff\'s Laws',
      'Fluid Mechanics & Bernoulli\'s Principle',
      'Engineering Thermodynamics & Heat Transfer',
      'Digital Electronics & Microprocessors',
      'Control Systems & Transfer Functions'
    ],
    Economics: [
      'Advanced Microeconomic Theory',
      'Macroeconomic Modeling & Policy',
      'Econometrics & Regression Analysis',
      'International Trade and Finance',
      'Development Economics'
    ],
    Statistics: [
      'Probability Theory & Random Variables',
      'Statistical Inference - Estimation & Hypothesis Testing',
      'Linear Regression & ANOVA',
      'Time Series Analysis & Forecasting'
    ],
    'Logical Reasoning': [
      'Analytical Reasoning & Critical Thinking',
      'Advanced Data Interpretation & Caselets',
      'Deductive & Inductive Logic',
      'Quantitative Aptitude & Mathematical Logic'
    ]
  },

  'Competitive Exam / General': {
    Mathematics: [
      'Quadratic Equations & Complex Numbers',
      'Calculus - Limits, Derivatives & Integrals',
      'Coordinate Geometry - Straight Lines, Circles & Conics',
      'Vectors and Three Dimensional Geometry',
      'Trigonometry & Inverse Trigonometric Functions',
      'Permutations, Combinations & Probability',
      'Matrices and Determinants',
      'Sequences and Series'
    ],
    Physics: [
      'Kinematics & Newton\'s Laws of Motion',
      'Work, Power, Energy & Rotational Dynamics',
      'Thermodynamics & Kinetic Theory',
      'Electrostatics, Current Electricity & Magnetism',
      'Optics (Ray & Wave Optics)',
      'Modern Physics, Atoms & Nuclei',
      'Oscillations and Waves'
    ],
    Chemistry: [
      'General Organic Chemistry (GOC) & Mechanisms',
      'Physical Chemistry - Mole Concept, Thermodynamics & Kinetics',
      'Inorganic Chemistry - Periodic Table, Chemical Bonding & Coordination Compounds',
      'Electrochemistry & Chemical Equilibrium',
      'Hydrocarbons & Functional Group Reactions'
    ],
    Biology: [
      'Human Physiology & Anatomy',
      'Genetics, Molecular Biology & Inheritance',
      'Plant Physiology & Photosynthesis',
      'Ecology, Environment & Biodiversity',
      'Reproduction in Organisms',
      'Biotechnology & Applications'
    ],
    'Computer Science': [
      'Data Structures & Algorithms for Technical Interviews',
      'Object-Oriented Programming (OOP) Concepts',
      'SQL & Database Querying',
      'System Design Basics & Networking'
    ],
    'Logical Reasoning': [
      'Syllogisms & Deductive Logic',
      'Blood Relations & Direction Sense',
      'Seating Arrangement & Puzzles',
      'Data Interpretation (Charts, Tables, Graphs)',
      'Number Series & Coding-Decoding'
    ],
    'Economics': [
      'Indian Economy & Banking Systems',
      'Inflation, GDP & Monetary Policy',
      'Budget, Taxation & Fiscal Policy',
      'International Trade & Global Organizations'
    ]
  }
};

/**
 * Returns curriculum-accurate default syllabus chapters for any Subject, Academic Level, and Board.
 */
export function getDefaultSyllabusChapters(
  subject: string,
  classLevel: string,
  board?: string
): string[] {
  // Normalize subject
  const normalizedSubject = subject.trim();
  const levelKey = SYLLABUS_DATABASE[classLevel] ? classLevel : 'Class 10';
  const levelData = SYLLABUS_DATABASE[levelKey];

  if (levelData && levelData[normalizedSubject]) {
    return levelData[normalizedSubject];
  }

  // Check STEM fallback
  if (normalizedSubject.toLowerCase().includes('math')) {
    return levelData['Mathematics'] || SYLLABUS_DATABASE['Class 10']['Mathematics'];
  }
  if (normalizedSubject.toLowerCase().includes('physic')) {
    return levelData['Physics'] || SYLLABUS_DATABASE['Class 10']['Physics'];
  }
  if (normalizedSubject.toLowerCase().includes('chem')) {
    return levelData['Chemistry'] || SYLLABUS_DATABASE['Class 10']['Chemistry'];
  }
  if (normalizedSubject.toLowerCase().includes('bio')) {
    return levelData['Biology'] || SYLLABUS_DATABASE['Class 10']['Biology'];
  }
  if (normalizedSubject.toLowerCase().includes('computer') || normalizedSubject.toLowerCase().includes('code') || normalizedSubject.toLowerCase().includes('program')) {
    return levelData['Computer Science'] || levelData['Programming'] || SYLLABUS_DATABASE['Class 10']['Computer Science'];
  }
  if (normalizedSubject.toLowerCase().includes('econ') || normalizedSubject.toLowerCase().includes('commerce')) {
    return levelData['Economics'] || SYLLABUS_DATABASE['Class 10']['Economics'];
  }
  if (normalizedSubject.toLowerCase().includes('history') || normalizedSubject.toLowerCase().includes('civic') || normalizedSubject.toLowerCase().includes('social')) {
    return levelData['History & Civics'] || SYLLABUS_DATABASE['Class 10']['History & Civics'];
  }
  if (normalizedSubject.toLowerCase().includes('geograph')) {
    return levelData['Geography'] || SYLLABUS_DATABASE['Class 10']['Geography'];
  }
  if (normalizedSubject.toLowerCase().includes('stat')) {
    return levelData['Statistics'] || SYLLABUS_DATABASE['Class 10']['Statistics'];
  }
  if (normalizedSubject.toLowerCase().includes('reason') || normalizedSubject.toLowerCase().includes('aptitude')) {
    return levelData['Logical Reasoning'] || SYLLABUS_DATABASE['Class 10']['Logical Reasoning'];
  }

  // Generic fallback from Class 10 Math
  return SYLLABUS_DATABASE['Class 10']['Mathematics'];
}

/**
 * Validates whether a list of suggested chapters contains out-of-syllabus higher-grade topics for lower grades.
 * e.g., Class 10/9/8/7/6 Math cannot contain Calculus, 3D Geometry vectors, Matrices & Determinants, Differential Equations.
 */
function filterGradeAppropriateChapters(
  chapters: string[],
  subject: string,
  classLevel: string
): string[] {
  const isLowerGrade = ['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'].includes(classLevel);
  const isMath = subject.toLowerCase().includes('math');

  if (isLowerGrade && isMath) {
    const prohibitedKeywords = /\b(calculus|integration|derivative|derivatives|integrals|differential equation|3d geometry|vectors|matrices|matrix|determinant|determinants|limits and derivatives|hyperbola|ellipse|conic sections|continuity and differentiability)\b/i;
    
    const cleanChapters = chapters.filter(ch => !prohibitedKeywords.test(ch));
    if (cleanChapters.length >= 3) {
      return cleanChapters;
    }
    // If too many were filtered, return the authentic syllabus chapters
    return getDefaultSyllabusChapters(subject, classLevel);
  }

  return chapters;
}

/**
 * Fetches dynamic, curriculum-accurate chapter suggestions for a given Subject, Class Level, and Board.
 */
export async function fetchSuggestedChapters(
  subject: string,
  classLevel: string,
  board: string
): Promise<string[]> {
  const cacheKey = `${subject}__${classLevel}__${board}`.toLowerCase().trim();
  
  if (suggestionsCache.has(cacheKey)) {
    return suggestionsCache.get(cacheKey)!;
  }

  const prompt = `
  You are an expert academic curriculum advisor for ${board} ${classLevel}.
  CRITICAL: Provide chapters that STRICTLY belong to the official curriculum syllabus of ${board} ${classLevel} for the subject "${subject}".

  GRADE-SPECIFIC SYLLABUS GUIDELINES:
  - In Class 10 Mathematics (${board}): Syllabus includes Quadratic Equations, Arithmetic Progressions (AP), Real Numbers & Polynomials, Pair of Linear Equations in Two Variables, Coordinate Geometry, Triangles & Similarity, Introduction to Trigonometry & Heights and Distances, Circles, Surface Areas and Volumes, Statistics & Probability.
    STRICT RULE: Do NOT suggest Calculus, Integrals, Derivatives, Matrices, Determinants, or 3D Geometry for Class 10! Those belong to Class 12.
  - In Class 9 Mathematics (${board}): Number Systems, Polynomials, Coordinate Geometry, Linear Equations in Two Variables, Lines and Angles, Triangles, Quadrilaterals, Circles, Heron's Formula, Surface Areas and Volumes, Statistics.
  - In Class 11 Mathematics (${board}): Sets, Relations and Functions, Trigonometric Functions, Complex Numbers & Quadratic Equations, Permutations and Combinations, Binomial Theorem, Sequences and Series, Straight Lines, Conic Sections, Limits and Derivatives, Statistics, Probability.
  - In Class 12 Mathematics (${board}): Relations and Functions, Inverse Trigonometric Functions, Matrices & Determinants, Continuity and Differentiability, Applications of Derivatives (AOD), Integrals (Indefinite & Definite), Applications of Integrals, Differential Equations, Vector Algebra, 3D Geometry, Linear Programming (LPP), Probability.
  - In Class 10 Science (${board}): Light - Reflection and Refraction, Electricity, Magnetic Effects of Electric Current, Chemical Reactions and Equations, Acids Bases and Salts, Metals and Non-Metals, Carbon and its Compounds, Life Processes, Control and Coordination, How do Organisms Reproduce?, Heredity.

  Provide a list of 6-8 exact, official curriculum chapter titles for:
  - Subject: ${subject}
  - Academic Level: ${classLevel}
  - Board/Curriculum: ${board}

  Return strictly a JSON array of strings containing clean chapter titles.
  `;

  try {
    const userApiKey = typeof window !== 'undefined' ? localStorage.getItem('gemini_api_key') : null;
    const googleToken = typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null;
    const sessionId = typeof window !== 'undefined' ? localStorage.getItem('google_session_id') : null;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userApiKey && userApiKey.trim()) headers["x-gemini-api-key"] = userApiKey.trim();
    if (googleToken && googleToken.trim()) headers["x-google-access-token"] = googleToken.trim();
    if (sessionId && sessionId.trim()) headers["x-session-id"] = sessionId.trim();

    const response = await fetch("/api/gemini/generate", {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        systemInstruction: "You are a master academic curriculum advisor. Return strictly a JSON array of 6-8 exact, syllabus-accurate chapter names for the specified subject, grade level, and curriculum board. Strictly avoid suggesting higher-grade topics for lower grades.",
        responseMimeType: "application/json"
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch chapter suggestions: ${response.status}`);
    }

    const data = await response.json();
    const parsed = parseAndCleanJSON(data.text || "") as any;

    let chapters: string[] = [];
    if (Array.isArray(parsed)) {
      chapters = parsed.map(item => typeof item === 'string' ? item : item.chapter || item.title || String(item));
    } else if (parsed && Array.isArray(parsed.chapters)) {
      chapters = parsed.chapters;
    } else if (parsed && Array.isArray(parsed.topics)) {
      chapters = parsed.topics;
    }

    chapters = chapters.filter(c => typeof c === 'string' && c.trim().length > 0).slice(0, 8);
    chapters = filterGradeAppropriateChapters(chapters, subject, classLevel);

    if (chapters.length > 0) {
      suggestionsCache.set(cacheKey, chapters);
      return chapters;
    }
  } catch (err) {
    console.warn("[ChapterSuggestions] Dynamic fetch failed, using authentic syllabus defaults:", err);
  }

  const defaults = getDefaultSyllabusChapters(subject, classLevel, board);
  suggestionsCache.set(cacheKey, defaults);
  return defaults;
}

